// 三種面板共用的右上角麻將牌 X。
// === 使用圖片 ===
// 無 — 奶油牌面、綠色牌底、珊瑚紅 X 與腮紅均由 Phaser.Graphics 繪製。
import type Phaser from 'phaser';
import type { IKeyboardFocusTarget } from '@input/spatial-keyboard-navigator';
import { animateTo, reducedMotion, Motion } from './motion';

export const CloseButtonLayout = {
  insetX: 12, // 貼齊外框右上圓角
  insetY: 18, // 跨在框線上
  size: 60, // 小麻將牌的奶油色牌面
  hitSize: 82, // 手機縮放後仍易點擊
  radius: 17, // 柔和圓角
  tilt: 8, // 微歪的小牌，維持清楚的關閉符號
  baseOffset: 4, // 綠色牌底露出厚度
  cheekX: 18, // 腮紅左右位置
  cheekY: 16, // 腮紅高度
  cheekWidth: 9, // 腮紅寬
  cheekHeight: 5, // 腮紅高
  crossHalfSize: 8, // 短圓頭 X
} as const;
const INK = {
  face: 0xffedc9,
  hover: 0xfff5df,
  base: 0x36734f,
  edge: 0xd8c6a1,
  coral: 0xeb5147,
  blush: 0xefb4a0,
} as const;
const EDGE_WIDTH = 1; // 牌面細邊
const FOCUS_WIDTH = 2; // 焦點外框
const FOCUS_GAP = 6; // 焦點框距牌面
const CROSS_WIDTH = 6; // X 線寬
const PRESS_OFFSET = 2; // 按下位移
const FOCUS_LIFT = 4; // 滑過與鍵盤焦點抬升
const PRESS_SCALE = 0.98; // 按下縮放
const FOCUS_MS = 120; // 放開與焦點動畫時長

export interface ICloseButton extends IKeyboardFocusTarget {
  readonly root: Phaser.GameObjects.Container;
}

export function createCloseButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  onActivate: () => void
): ICloseButton {
  const root = scene.add.container(x, y).setName('panel-close');
  const visual = scene.add.container(0, 0);
  const closeArt = scene.add.graphics().setAngle(CloseButtonLayout.tilt);
  visual.add(closeArt);
  const hit = scene.add
    .zone(0, 0, CloseButtonLayout.hitSize, CloseButtonLayout.hitSize)
    .setInteractive({ useHandCursor: true });
  root.add([visual, hit]);
  let focused = false;
  let hovered = false;
  let pressed = false;
  const refresh = (): void => {
    if (!root.scene) return;
    const scale = pressed && !reducedMotion() ? PRESS_SCALE : 1;
    animateTo(
      scene,
      visual,
      {
        y: reducedMotion() ? 0 : pressed ? PRESS_OFFSET : focused || hovered ? -FOCUS_LIFT : 0,
        scaleX: scale,
        scaleY: scale,
      },
      pressed ? 0 : FOCUS_MS,
      Motion.ease
    );
    const half = CloseButtonLayout.size / 2;
    closeArt.clear();
    closeArt
      .fillStyle(INK.base)
      .fillRoundedRect(
        -half,
        -half + CloseButtonLayout.baseOffset,
        CloseButtonLayout.size,
        CloseButtonLayout.size,
        CloseButtonLayout.radius
      );
    closeArt
      .fillStyle(hovered ? INK.hover : INK.face)
      .fillRoundedRect(
        -half,
        -half,
        CloseButtonLayout.size,
        CloseButtonLayout.size,
        CloseButtonLayout.radius
      );
    closeArt
      .lineStyle(EDGE_WIDTH, INK.edge)
      .strokeRoundedRect(
        -half,
        -half,
        CloseButtonLayout.size,
        CloseButtonLayout.size,
        CloseButtonLayout.radius
      );
    closeArt.lineStyle(CROSS_WIDTH, INK.coral);
    closeArt.lineBetween(
      -CloseButtonLayout.crossHalfSize,
      -CloseButtonLayout.crossHalfSize,
      CloseButtonLayout.crossHalfSize,
      CloseButtonLayout.crossHalfSize
    );
    closeArt.lineBetween(
      CloseButtonLayout.crossHalfSize,
      -CloseButtonLayout.crossHalfSize,
      -CloseButtonLayout.crossHalfSize,
      CloseButtonLayout.crossHalfSize
    );
    closeArt.fillStyle(INK.coral);
    for (const x of [-CloseButtonLayout.crossHalfSize, CloseButtonLayout.crossHalfSize]) {
      for (const y of [-CloseButtonLayout.crossHalfSize, CloseButtonLayout.crossHalfSize]) {
        closeArt.fillCircle(x, y, CROSS_WIDTH / 2);
      }
    }
    closeArt.fillStyle(INK.blush);
    for (const side of [-1, 1]) {
      closeArt.fillEllipse(
        side * CloseButtonLayout.cheekX,
        CloseButtonLayout.cheekY,
        CloseButtonLayout.cheekWidth,
        CloseButtonLayout.cheekHeight
      );
    }
    if (focused)
      closeArt
        .lineStyle(FOCUS_WIDTH, INK.face)
        .strokeRoundedRect(
          -half - FOCUS_GAP,
          -half - FOCUS_GAP,
          CloseButtonLayout.size + FOCUS_GAP * 2,
          CloseButtonLayout.size + FOCUS_GAP * 2 + CloseButtonLayout.baseOffset,
          CloseButtonLayout.radius + FOCUS_GAP
        );
  };
  hit.on('pointerover', () => {
    hovered = true;
    refresh();
  });
  hit.on('pointerout', () => {
    hovered = false;
    pressed = false;
    refresh();
  });
  hit.on('pointerdown', () => {
    pressed = true;
    refresh();
  });
  hit.on('pointerupoutside', () => {
    pressed = false;
    refresh();
  });
  hit.on('pointerup', () => {
    const activate = pressed;
    pressed = false;
    refresh();
    if (activate) onActivate();
  });
  refresh();
  return {
    root,
    x,
    y,
    activate: onActivate,
    setFocused: (value) => {
      focused = value;
      refresh();
    },
  };
}
